import { AD_STATE, APPROVAL_STATE, CampaignData, CAMPAIGN_TYPE } from '@/lib/types';
import { isTaskTrackingSummary, isCampaignType } from '@/lib/utils';
import { campaignDataAtom, campaignTaskTrackingAtom } from '@/store';
import { useAtomValue } from 'jotai';

import { useSuspenseQuery } from '@tanstack/react-query';
import { allCampaignTaskTrackingQueryOptions } from '@/lib/query-options';

// Publish/sync task types that share the tracking summary but are NOT content
// generation — launching or syncing a campaign must not resurrect "Generating
// Campaign" UI on a finished campaign (#5664).
// Classifying an imported campaign's type is not generation either (#6612).
export const NON_GENERATION_TASK_KEYS = [
  'campaign_state_update',
  'campaign_reverse_sync',
  'campaign_ads_ga_sync',
  'external_campaign_classification',
];

export function useCampaignsTaskTracking(campaigns: CampaignData[]) {
  // Filter and sort campaigns
  const onboardingCampaigns = campaigns
    .filter((campaign) => isCampaignType(campaign, CAMPAIGN_TYPE.ONBOARDING))
    .sort((a, b) => b.id - a.id);

  const brandCampaigns = campaigns
    .filter((campaign) => isCampaignType(campaign, CAMPAIGN_TYPE.BRAND))
    .sort((a, b) => b.id - a.id);

  // Ensure campaignIds is never empty
  const campaignIds: number[] = [];
  if (onboardingCampaigns.length > 0) campaignIds.push(onboardingCampaigns[0].id);
  if (brandCampaigns.length > 0) campaignIds.push(brandCampaigns[0].id);

  // Fetch task tracking data
  const { data: allCampaignTaskTracking } = useSuspenseQuery(allCampaignTaskTrackingQueryOptions(campaignIds));

  // Calculate total tasks and in-progress tasks. Stuck rows are subtracted so that an
  // orphaned worker doesn't keep the onboarding "Generating…" screen hanging forever —
  // same reasoning as useAreAllTasksCompleted below.
  const { sumTotal, taskProcessing } = Object.values(allCampaignTaskTracking).reduce(
    (acc, cur) => {
      return {
        sumTotal:
          acc.sumTotal +
          Object.entries(cur).reduce((innerAcc, [key, innerCur]) => {
            if (NON_GENERATION_TASK_KEYS.includes(key)) return innerAcc;
            return isTaskTrackingSummary(innerCur) ? innerAcc + innerCur.total : innerAcc;
          }, 0),
        taskProcessing:
          acc.taskProcessing +
          Object.entries(cur).reduce((innerAcc, [key, innerCur]) => {
            if (NON_GENERATION_TASK_KEYS.includes(key)) return innerAcc;
            return isTaskTrackingSummary(innerCur)
              ? innerAcc + Math.max(0, innerCur.in_progress - (innerCur.stuck_in_progress ?? 0))
              : innerAcc;
          }, 0),
      };
    },
    { sumTotal: 0, taskProcessing: 0 },
  );

  return {
    sumTotal,
    taskProcessing,
  };
}

const useAreAllTasksCompleted = () => {
  const campaignTaskTracking = useAtomValue(campaignTaskTrackingAtom);

  // Return object with loading state to prevent flickering
  if (!campaignTaskTracking) return { isLoading: true, areCompleted: false };

  // is_stale = campaign created > 1h ago. Any IN_PROGRESS rows at this age can only be
  // orphans the reaper hasn't caught yet, so render the campaign instead of hiding it.
  // Short-circuit before the reduce so we don't waste cycles tallying counts we'll discard.
  if (campaignTaskTracking.is_stale) return { isLoading: false, areCompleted: true };

  // If no creation tasks exist, campaign is ready
  if (campaignTaskTracking.campaign_creation.total === 0) return { isLoading: false, areCompleted: true };

  // Stuck tasks (worker died, orphaned IN_PROGRESS rows older than the backend's 20-min
  // threshold) are subtracted from in_progress. The backend reaper marks them FAILED on a
  // 15-min beat schedule, but in the gap between worker-crash and reaper-run they would
  // otherwise gate the entire campaign view behind a generation job that isn't running.
  const { sumTotal, taskProcessing } = Object.entries(campaignTaskTracking).reduce(
    (acc, [key, value]) => {
      if (NON_GENERATION_TASK_KEYS.includes(key)) return acc;
      if (isTaskTrackingSummary(value)) {
        acc.sumTotal += value.total;
        acc.taskProcessing += Math.max(0, value.in_progress - (value.stuck_in_progress ?? 0));
      }
      return acc;
    },
    { sumTotal: 0, taskProcessing: 0 },
  );

  return { isLoading: false, areCompleted: (sumTotal > 0 && taskProcessing === 0) };
};

export const useAreAllStatesApproved = (campaignData?: CampaignData) => {
  // Hooks must run unconditionally — `campaignData ?? useAtomValue(...)`
  // short-circuits the hook call and changes hook order between renders.
  const atomCampaign = useAtomValue(campaignDataAtom);
  const campaign = campaignData ?? atomCampaign;
  if (!campaign) return false;

  // Convert states to uppercase for consistent comparison
  const APPROVED_STATE = APPROVAL_STATE.APPROVED.toUpperCase();
  const APPROVED_AD_STATE = AD_STATE.APPROVED.toUpperCase();

  return (
    campaign.budget_state.toUpperCase() === APPROVED_STATE &&
    campaign.assets_state.toUpperCase() === APPROVED_STATE &&
    campaign.settings_state.toUpperCase() === APPROVED_STATE &&
    campaign.ads.every((ad) => ad.state.toUpperCase() === APPROVED_AD_STATE)
  );
};

export const useAreAllStatesExceptBudgetApproved = (campaignData?: CampaignData) => {
  const atomCampaign = useAtomValue(campaignDataAtom);
  const campaign = campaignData ?? atomCampaign;
  if (!campaign) return false;

  // Convert states to uppercase for consistent comparison
  const APPROVED_STATE = APPROVAL_STATE.APPROVED.toUpperCase();
  const APPROVED_AD_STATE = AD_STATE.APPROVED.toUpperCase();

  return (
    campaign.assets_state.toUpperCase() === APPROVED_STATE &&
    campaign.settings_state.toUpperCase() === APPROVED_STATE &&
    campaign.ads.every((ad) => ad.state.toUpperCase() === APPROVED_AD_STATE)
  );
};

export const useCampaignStateLabel = () => {
  // Both hooks run before any early return so hook order is stable as the
  // campaign atom transitions between null and a value.
  const campaign = useAtomValue(campaignDataAtom);
  const { areCompleted: areAllTasksCompleted } = useAreAllTasksCompleted();

  if (!campaign) return '';
  if (!areAllTasksCompleted) return 'Creating the Campaign';
  if (campaign.state === 'draft') return 'Approve Changes and launch';
  return 'Creating the Campaign';
};

export default useAreAllTasksCompleted;
